// src/pages/ResetPassword.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Link,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenbody, setTokenbody] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get token from URL query parameter
  const { token } = useParams();

  useEffect(() => {
  const validateToken = async () => {
    // Verify token exists
    if (!token) {
      setTokenValid(false);
      setError("Invalid or missing reset token");
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/auth/validate-reset-token/${token}`
      );
      setTokenbody(response.data);
    } catch (err) {
      setTokenValid(false);
      setError("Invalid or expired reset token");
    }
  };

  validateToken();
}, [token]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(pwd)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(pwd)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(pwd)) {
      return "Password must contain at least one number";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Validation
    if (!password.trim() || !confirmPassword.trim()) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/auth/reset-password`,
        { 
          token,
          password: password, 
          confirmPassword: password, 
        }
      );

      setSuccess(true);
      toast.success("Password reset successfully!");
      
      // Redirect to sign in after 3 seconds
      setTimeout(() => {
        navigate("/signin");
      }, 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to reset password. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      
      // If token is invalid/expired, mark it
      if (err.response?.status === 400 || err.response?.status === 401) {
        setTokenValid(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #111827  0%, #121b30ff 100%)",
        padding: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          width: "100%",
          maxWidth: 450,
          borderRadius: 2,
          backgroundColor: "#1f2937",
        }}
      >
        <Stack spacing={3}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#00BFFF",
                marginBottom: 1,
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#9ca3af",
              }}
            >
              {success
                ? "Your password has been reset"
                :  `Enter your new password for ${tokenbody?.user?.email}`}
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                backgroundColor: "#7f1d1d",
                color: "#fecaca",
                "& .MuiAlert-icon": { color: "#fecaca" },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{
                backgroundColor: "#065f46",
                color: "#d1fae5",
                "& .MuiAlert-icon": { color: "#d1fae5" },
              }}
            >
              Password reset successfully! Redirecting to sign in...
            </Alert>
          )}

          {!success && tokenValid && (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  disabled={isLoading}
                  variant="outlined"
                  InputLabelProps={{
                    style: { color: "#ffffffff", fontSize: "1.1rem" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "#9ca3af" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      color: "#00BFFF",
                      backgroundColor: "#374151",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6b7280",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d6c1c1ff",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00BFFF",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.6,
                      },
                    },
                  }}
                />

                <TextField
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  disabled={isLoading}
                  variant="outlined"
                  InputLabelProps={{
                    style: { color: "#ffffffff", fontSize: "1.1rem" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: "#9ca3af" }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      color: "#00BFFF",
                      backgroundColor: "#374151",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6b7280",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d6c1c1ff",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00BFFF",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.6,
                      },
                    },
                  }}
                />

                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Password must contain:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
                    • At least 8 characters
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
                    • One uppercase and one lowercase letter
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
                    • At least one number
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    backgroundColor: "#00BFFF",
                    color: "#fff",
                    fontWeight: 600,
                    padding: "12px",
                    "&:hover": {
                      backgroundColor: "#0099CC",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#374151",
                      color: "#6b7280",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "RESET PASSWORD"
                  )}
                </Button>
              </Stack>
            </Box>
          )}

          {!tokenValid && (
            <Alert
              severity="error"
              sx={{
                backgroundColor: "#7f1d1d",
                color: "#fecaca",
                "& .MuiAlert-icon": { color: "#fecaca" },
              }}
            >
              This password reset link is invalid or has expired. Please request a new one.
            </Alert>
          )}

          <Box sx={{ textAlign: "center" }}>
            <Link
              onClick={() => navigate("/signin")}
              sx={{
                color: "#00BFFF",
                textDecoration: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <ArrowBack fontSize="small" />
              Back to Sign In
            </Link>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ResetPassword;